import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { User, IdCard, Phone, MapPin, Calendar, Save, BadgeCheck } from "lucide-react";

export default function Profile() {
    const { user, setUser } = useAuth();
    const [form, setForm] = useState({});
    const [provinces, setProvinces] = useState([]);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");

    useEffect(() => {
        if (user) {
            setForm({
                full_name: user.full_name || "",
                cccd: user.cccd || "",
                phone: user.phone || "",
                province: user.province || "",
                date_of_birth: user.date_of_birth || "",
                gender: user.gender || "",
            });
        }
        api.get("/profile/provinces").then((r) => setProvinces(r.data.provinces || []));
    }, [user]);

    const save = async () => {
        setSaving(true); setMsg(""); setErr("");
        try {
            const { data } = await api.put("/profile", form);
            setUser(data);
            setMsg("Đã lưu hồ sơ thành công");
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    if (!user) return null;

    return (
        <main className="pt-24 pb-12 min-h-screen">
            <div className="container-x max-w-4xl">
                <div className="mb-8 fade-up">
                    <h1 className="font-heading text-3xl font-bold">Hồ sơ thí sinh</h1>
                    <p className="text-slate-500 mt-2">Cập nhật thông tin để được công nhận thí sinh chính thức.</p>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                    <div className="card p-6 text-center fade-up delay-1">
                        <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-[var(--gold)]/10 border-2 border-[var(--gold)]/40 flex items-center justify-center overflow-hidden">
                            {user.avatar_url ? <img src={user.avatar_url} className="w-full h-full object-cover" alt="" />
                                : <User className="w-10 h-10 text-[var(--gold-deep)]" />}
                        </div>
                        <h3 className="font-heading font-semibold">{user.full_name}</h3>
                        <p className="text-xs text-slate-500 mt-1">{user.email}</p>
                        <div className="mt-4 space-y-2">
                            <div className="badge badge-gold w-full justify-center">{roleLabel(user.role)}</div>
                            {user.candidate_code && (
                                <div className="text-xs">
                                    <div className="text-slate-500 uppercase tracking-widest">Mã dự thi</div>
                                    <div className="font-mono text-[var(--gold-deep)] mt-1">{user.candidate_code}</div>
                                </div>
                            )}
                        </div>
                        <div className="border-t border-[var(--border)] mt-5 pt-4 text-xs text-slate-500 flex items-center justify-center gap-2">
                            <BadgeCheck className="w-3 h-3" /> Đăng nhập qua: <span className="text-[var(--navy)]">{providerLabel(user.auth_provider)}</span>
                        </div>
                    </div>

                    <div className="md:col-span-2 card p-6 space-y-4 fade-up delay-2">
                        <h3 className="font-heading font-semibold text-lg mb-2">Thông tin cá nhân</h3>

                        <Field icon={<User />} label="Họ và tên" value={form.full_name}
                            onChange={(v) => setForm({ ...form, full_name: v })} testId="full-name-input" />
                        <Field icon={<IdCard />} label="Số CCCD" value={form.cccd}
                            onChange={(v) => setForm({ ...form, cccd: v })} placeholder="12 chữ số" testId="cccd-input" />
                        <Field icon={<Phone />} label="Số điện thoại" value={form.phone}
                            onChange={(v) => setForm({ ...form, phone: v })} placeholder="0xxxxxxxxx" testId="phone-input" />

                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">Tỉnh/Thành phố</label>
                                <div className="relative">
                                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                    <select value={form.province || ""} onChange={(e) => setForm({ ...form, province: e.target.value })}
                                        className="input pl-10" data-testid="province-select">
                                        <option value="">-- Chọn tỉnh thành --</option>
                                        {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">Ngày sinh</label>
                                <div className="relative">
                                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                    <input type="date" value={form.date_of_birth || ""}
                                        onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                                        className="input pl-10" data-testid="dob-input" />
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">Giới tính</label>
                            <div className="flex gap-2">
                                {[
                                    { v: "male", l: "Nam" }, { v: "female", l: "Nữ" }, { v: "other", l: "Khác" },
                                ].map((g) => (
                                    <button key={g.v} type="button" onClick={() => setForm({ ...form, gender: g.v })}
                                        className={`flex-1 py-2 rounded-lg border text-sm transition ${
                                            form.gender === g.v ? "border-[var(--gold)] bg-[var(--gold)]/10 text-[var(--gold-deep)]"
                                                : "border-[var(--border)] text-slate-600 hover:border-[var(--border-strong)]"}`}
                                        data-testid={`gender-${g.v}`}>
                                        {g.l}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {msg && <div className="text-[var(--gold-deep)] text-sm bg-[var(--gold)]/10 border border-[var(--gold)]/30 rounded-lg p-3">{msg}</div>}
                        {err && <div className="text-red-400 text-sm bg-red-400/10 border border-red-400/30 rounded-lg p-3">{err}</div>}

                        <button onClick={save} disabled={saving} className="btn-primary w-full" data-testid="save-profile-btn">
                            <Save className="w-4 h-4" /> {saving ? "Đang lưu…" : "Lưu hồ sơ"}
                        </button>
                    </div>
                </div>
            </div>
        </main>
    );
}

function Field({ icon, label, value, onChange, placeholder, testId }) {
    return (
        <div>
            <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">{label}</label>
            <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 [&_svg]:w-4 [&_svg]:h-4">{icon}</span>
                <input value={value || ""} onChange={(e) => onChange(e.target.value)}
                    className="input pl-10" placeholder={placeholder} data-testid={testId} />
            </div>
        </div>
    );
}

function roleLabel(r) {
    return { super_admin: "Quản trị tối cao", btc_admin: "Ban Tổ chức", jury: "Ban Giám khảo", participant: "Thí sinh", voter: "Người vote" }[r] || r;
}
function providerLabel(p) {
    return { email: "Email/Mật khẩu", google: "Google", facebook_mock: "Facebook (Demo)" }[p] || p;
}
